import {
  type AlarmKind,
  type AlarmSession,
  type CheckinStatus,
  type ClassRollCall,
  minutesSince,
  shortDate,
  timeOf,
  todayIso,
  useActiveAlarm,
  useAlarmActions,
  useAlarmBoard,
  useAlarmHistory,
  useAnnouncementActions,
  useCurrentContext,
  useLessons,
} from '@shkola/api';
import { GlobeHemisphereEastIcon } from '@phosphor-icons/react/GlobeHemisphereEast';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShelterTasks } from '../care/ShelterTasks';
import { Breathing } from '../care/shared';
import { ChildLine, SirenMark, alarmGround, useNow } from '../components/AlarmBanner';
import { ControlHour } from '../components/ControlHour';
import { EmptyState, Select, Spinner } from '../components/ui';
import {
  Block,
  DEEP,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { CheckIcon, PaperPlaneTiltIcon, SirenIcon } from '../design/icons';

/*
 * «Тривога в школі» in «Форми 2» (28.10). Calm: a mint card «Зараз тривоги немає» with what the page will
 * hold when one starts; for the director the start, the control hour and the history. During an alarm:
 * the band of the home across the top, big — the kind, since when and how long, what to do now (on orange
 * with white words; a drill on butter); the director's figures with those not found or in need on coral
 * first; the classes as small cards (mint once in the shelter); the roll-call with a status to tap for
 * each student; the student breathes and reads; the family sees whether the child is in the shelter.
 */

const STATUSES: CheckinStatus[] = ['in_shelter', 'absent', 'not_found', 'needs_help'];
/** a status's colour: in the shelter mint, away lavender, not found coral, needs help apricot */
const STATUS_TONE: Record<CheckinStatus, Grad> = {
  in_shelter: 'mint',
  absent: 'lavender',
  not_found: 'coral',
  needs_help: 'apricot',
  safe_elsewhere: 'sky',
};

/** A label as a soft rectangle in a colour. */
function Mark({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[8px] px-2.5 text-[13px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** A bar of how far: the deep gradient of its colour on a light track. */
function Bar({ share, tone, track = PAPER }: { share: number; tone: Grad; track?: string }) {
  const [from, to] = DEEP[tone];
  return (
    <span className="block h-2 overflow-hidden rounded-full" style={{ backgroundColor: track }}>
      <span
        className="block h-full rounded-full transition-[width] duration-700"
        style={{
          width: `${Math.max(0, Math.min(100, share * 100))}%`,
          backgroundImage: `linear-gradient(90deg, ${from} 0%, ${to} 100%)`,
        }}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------------------------
// Roll call of one class (teachers, and the administration for any class)
// ---------------------------------------------------------------------------------------------
function RollCall({
  session,
  schoolId,
  classes,
  shelters,
  defaultClassId,
}: {
  session: AlarmSession;
  schoolId: string;
  classes: ClassRollCall[];
  shelters: Array<{ id: string; name: string }>;
  defaultClassId: string | undefined;
}) {
  const { t } = useTranslation();
  const actions = useAlarmActions();
  const [classId, setClassId] = useState(defaultClassId ?? classes[0]?.classId);
  const klass = classes.find((c) => c.classId === classId);
  const [shelterId, setShelterId] = useState<string>();
  if (!klass) return null;
  const shelter = shelterId ?? klass.shelterId ?? shelters[0]?.id ?? null;
  const unmarked = klass.students.filter((s) => !s.status);
  const marked = klass.students.length - unmarked.length;

  return (
    <Block>
      <Head title={t('alarm.rollCall')} note={`${marked}/${klass.students.length}`}>
        <Select
          aria-label={t('alarm.myClass')}
          value={klass.classId}
          onChange={(e) => setClassId(e.target.value)}
        >
          {classes.map((c) => (
            <option key={c.classId} value={c.classId}>
              {c.classLabel}
            </option>
          ))}
        </Select>
      </Head>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {klass.confirmedAt ? (
          <Mark tone="mint">
            <CheckIcon size={13} weight="bold" aria-hidden />
            {t('alarm.weAreInShelter')} · {klass.shelterName} · {timeOf(klass.confirmedAt)}
          </Mark>
        ) : (
          <>
            <Select
              aria-label={t('alarm.shelters')}
              value={shelter ?? ''}
              onChange={(e) => setShelterId(e.target.value)}
              className="!h-11"
            >
              {shelters.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <button
              type="button"
              className={pillClass('dark')}
              onClick={() =>
                actions.confirm.mutate({
                  schoolId,
                  sessionId: session.id,
                  classId: klass.classId,
                  shelterId: shelter,
                })
              }
            >
              {t('alarm.weAreInShelter')}
              <PillIcon icon={CheckIcon} />
            </button>
          </>
        )}
      </div>
      <ul className="flex flex-col">
        {klass.students.map((s) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 py-2.5"
            style={{ borderTop: `1px solid ${LINE}` }}
          >
            <span className="min-w-0 text-[15px]" style={{ color: INK }}>
              {s.lastName} {s.firstName}
            </span>
            <span
              role="radiogroup"
              aria-label={`${s.lastName} ${s.firstName}`}
              className="grid w-full grid-cols-2 gap-0.5 rounded-[18px] p-1 sm:flex sm:w-auto sm:flex-wrap"
              style={{ backgroundColor: PAPER }}
            >
              {(s.status === 'safe_elsewhere'
                ? (['safe_elsewhere', ...STATUSES] as CheckinStatus[])
                : STATUSES
              ).map((status) => {
                const on = s.status === status;
                const tone = STATUS_TONE[status];
                return (
                  <button
                    key={status}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() =>
                      actions.mark.mutate({
                        schoolId,
                        sessionId: session.id,
                        marks: [{ studentId: s.id, status }],
                      })
                    }
                    className={`h-7 rounded-full px-2.5 text-[12.5px] whitespace-nowrap transition-colors ${on ? 'font-medium' : 'hover:bg-white'}`}
                    style={
                      on
                        ? { backgroundImage: grad(tone), color: GRAD[tone].ink }
                        : { color: INK_SOFT }
                    }
                  >
                    {t(`alarm.status.${status}`)}
                  </button>
                );
              })}
            </span>
          </li>
        ))}
      </ul>
      {klass.abroad.length > 0 && (
        <p
          className="mt-3 flex items-start gap-2 text-[14px] leading-5"
          style={{ color: INK_SOFT }}
        >
          <GlobeHemisphereEastIcon
            size={16}
            weight="bold"
            aria-hidden
            className="mt-0.5 shrink-0"
          />
          {t('abroad.rollCall', {
            names: klass.abroad.map((s) => `${s.lastName} ${s.firstName}`).join(', '),
          })}
        </p>
      )}
      {(actions.confirm.isError || actions.mark.isError || actions.complete.isError) && (
        <p role="alert" className="mt-3 text-[14px] font-medium" style={{ color: GRAD.coral.ink }}>
          {t('common.error')}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2 empty:hidden">
        {unmarked.length > 0 && (
          <button
            type="button"
            className={pillClass('light')}
            onClick={() =>
              actions.mark.mutate({
                schoolId,
                sessionId: session.id,
                marks: unmarked.map((s) => ({ studentId: s.id, status: 'in_shelter' as const })),
              })
            }
          >
            {t('alarm.allPresent')} ({unmarked.length})
            <PillIcon icon={CheckIcon} />
          </button>
        )}
        {klass.confirmedAt && !klass.completedAt && unmarked.length === 0 && (
          <button
            type="button"
            className={pillClass('light')}
            onClick={() =>
              actions.complete.mutate({ sessionId: session.id, classId: klass.classId })
            }
          >
            {t('alarm.complete')}
            <PillIcon icon={CheckIcon} />
          </button>
        )}
      </div>
    </Block>
  );
}

// ---------------------------------------------------------------------------------------------
// The director's dashboard
// ---------------------------------------------------------------------------------------------
function Board({
  session,
  classes,
  shelters,
}: {
  session: AlarmSession;
  classes: ClassRollCall[];
  shelters: Array<{ id: string; name: string; capacity: number; occupied: number }>;
}) {
  const { t } = useTranslation();
  const students = classes.flatMap((c) =>
    c.students.map((s) => ({ ...s, classLabel: c.classLabel })),
  );
  const count = (status: CheckinStatus) => students.filter((s) => s.status === status).length;
  const notFound = students.filter((s) => s.status === 'not_found');
  const help = students.filter((s) => s.status === 'needs_help');
  const tiles: Array<{ label: string; value: string | number; alert?: Grad }> = [
    {
      label: t('alarm.stats.classes'),
      value: `${classes.filter((c) => c.confirmedAt).length}/${classes.length}`,
    },
    { label: t('alarm.stats.students'), value: `${count('in_shelter')}/${students.length}` },
    { label: t('alarm.stats.unmarked'), value: students.filter((s) => !s.status).length },
    {
      label: t('alarm.stats.notFound'),
      value: notFound.length,
      alert: notFound.length > 0 ? 'coral' : undefined,
    },
    {
      label: t('alarm.stats.needsHelp'),
      value: help.length,
      alert: help.length > 0 ? 'apricot' : undefined,
    },
  ];
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
        {tiles.map((tile, i) => (
          <div
            key={tile.label}
            className={`flex min-h-[104px] min-w-0 flex-col justify-between rounded-[24px] p-4 md:min-h-[112px] md:p-5 ${i === tiles.length - 1 ? 'col-span-2 sm:col-span-1' : ''}`}
            style={
              tile.alert
                ? { backgroundImage: grad(tile.alert), color: GRAD[tile.alert].ink }
                : { backgroundColor: '#FFFFFF', color: INK_SOFT }
            }
          >
            <span className="text-[13.5px] leading-[18px] font-medium">{tile.label}</span>
            <span
              className="font-display text-[34px] leading-[40px] font-light tracking-[-0.04em] tabular-nums md:text-[44px] md:leading-[48px]"
              style={{ color: INK }}
            >
              {tile.value}
            </span>
          </div>
        ))}
      </div>
      {(notFound.length > 0 || help.length > 0) && (
        <section
          className="grid grid-cols-1 gap-5 rounded-[28px] p-5 sm:grid-cols-2 md:p-6"
          style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
        >
          {(
            [
              [t('alarm.stats.notFound'), notFound],
              [t('alarm.stats.needsHelp'), help],
            ] as const
          ).map(
            ([title, people]) =>
              people.length > 0 && (
                <div key={title} className="min-w-0">
                  <p className="text-[14px] font-medium">
                    {title} · {people.length}
                  </p>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {people.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-3 rounded-[14px] bg-white/80 px-3.5 py-2 text-[15px]"
                        style={{ color: INK }}
                      >
                        <span className="min-w-0 truncate">
                          {s.lastName} {s.firstName}
                        </span>
                        <span className="shrink-0 text-[13px]" style={{ color: INK_SOFT }}>
                          {s.classLabel}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
          )}
        </section>
      )}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <Block className="lg:col-span-2">
          <Head title={t('alarm.classesTitle')} />
          <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
            {classes.map((c) => {
              const marked = c.students.filter((s) => s.status).length;
              const done = Boolean(c.confirmedAt);
              return (
                <div
                  key={c.classId}
                  className="flex min-w-0 flex-col gap-2 rounded-[20px] p-3.5"
                  style={
                    done
                      ? { backgroundImage: grad('mint'), color: GRAD.mint.ink }
                      : { backgroundColor: PAPER, color: INK_SOFT }
                  }
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className="font-display text-[24px] leading-7 font-light tracking-[-0.03em]"
                      style={{ color: INK }}
                    >
                      {c.classLabel}
                    </span>
                    <span className="truncate text-[12.5px]">{c.shelterName}</span>
                  </div>
                  <p
                    className="text-[13px] font-medium"
                    style={{ color: done ? GRAD.mint.ink : GRAD.coral.ink }}
                  >
                    {done
                      ? t('alarm.confirmed', { time: timeOf(c.confirmedAt as string) })
                      : t('alarm.notConfirmed')}
                  </p>
                  <div className="flex items-center gap-2.5">
                    <span className="flex-1">
                      <Bar
                        share={c.students.length ? marked / c.students.length : 0}
                        tone="mint"
                        track={done ? 'rgb(255 255 255 / 0.7)' : '#FFFFFF'}
                      />
                    </span>
                    <span className="text-[12.5px] tabular-nums" style={{ color: INK }}>
                      {marked}/{c.students.length}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Block>
        <Block>
          <Head title={t('alarm.shelters')} />
          <ul className="flex flex-col gap-4">
            {shelters.map((s) => (
              <li key={s.id}>
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <span className="text-[15px] font-medium" style={{ color: INK }}>
                    {s.name}
                  </span>
                  <span className="text-[13px] tabular-nums" style={{ color: INK_SOFT }}>
                    {t('alarm.occupancy', { occupied: s.occupied, capacity: s.capacity })}
                  </span>
                </div>
                <Bar share={s.capacity ? s.occupied / s.capacity : 0} tone="lavender" />
              </li>
            ))}
          </ul>
        </Block>
      </div>
      <Templates schoolId={session.school_id} />
    </>
  );
}

function Templates({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const publish = useAnnouncementActions().publish;
  const [sent, setSent] = useState<string[]>([]);
  return (
    <Block>
      <Head title={t('alarm.templates')} />
      <ul className="flex flex-col gap-2">
        {(['start', 'end', 'online'] as const).map((key) => (
          <li
            key={key}
            className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] px-4 py-3"
            style={{ backgroundColor: PAPER }}
          >
            <p
              className="min-w-0 flex-1 basis-72 text-[14.5px] leading-[21px]"
              style={{ color: INK }}
            >
              {t(`alarm.templateTexts.${key}`)}
            </p>
            {sent.includes(key) ? (
              <Mark tone="mint">
                <CheckIcon size={13} weight="bold" aria-hidden />
                {t('alarm.sent')}
              </Mark>
            ) : (
              <button
                type="button"
                className={pillClass('light', 'sm')}
                onClick={() =>
                  void publish
                    .mutateAsync({
                      schoolId,
                      audience: 'school',
                      classId: null,
                      title: t('alarm.title'),
                      body: t(`alarm.templateTexts.${key}`),
                      requiresAck: false,
                    })
                    .then(() => setSent([...sent, key]))
                }
              >
                {t('alarm.send')}
                <PillIcon icon={PaperPlaneTiltIcon} size="sm" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </Block>
  );
}

function History({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const history = useAlarmHistory(schoolId);
  return (
    <Block>
      <Head title={t('alarm.history')} />
      {history.isPending ? (
        <Spinner />
      ) : (history.data ?? []).length === 0 ? (
        <EmptyState>—</EmptyState>
      ) : (
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[520px] text-[14px]" style={{ color: INK }}>
            <thead>
              <tr className="text-left text-[12.5px]" style={{ color: INK_FAINT }}>
                <th className="pb-2 font-medium">{t('alarm.historyColumns.date')}</th>
                <th className="pb-2 font-medium">{t('alarm.historyColumns.kind')}</th>
                <th className="pb-2 text-right font-medium">
                  {t('alarm.historyColumns.duration')}
                </th>
                <th className="pb-2 text-right font-medium">{t('alarm.historyColumns.lessons')}</th>
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).map((s) => (
                <tr key={s.id} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td className="py-2.5">
                    {shortDate(
                      new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv' }).format(
                        new Date(s.started_at),
                      ),
                    )}
                    <span style={{ color: INK_SOFT }}>
                      , {timeOf(s.started_at)}–{timeOf(s.ended_at as string)}
                    </span>
                  </td>
                  <td>{t(`alarm.kinds.${s.kind}`)}</td>
                  <td className="text-right tabular-nums">
                    {t('alarm.minutes', { count: s.minutes })}
                  </td>
                  <td className="text-right tabular-nums">{s.lessonsInShelter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Block>
  );
}

function StartPanel({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const start = useAlarmActions().start;
  const kinds: AlarmKind[] = ['drill', 'air', 'mining', 'evacuation'];
  return (
    <Block>
      <Head title={t('alarm.start.title')} />
      <div className="flex flex-wrap gap-2">
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            disabled={start.isPending}
            onClick={() => start.mutate({ schoolId, kind })}
            className={`${pillClass('light')} disabled:opacity-50`}
          >
            {t(`alarm.start.${kind}`)}
            <PillIcon icon={SirenIcon} />
          </button>
        ))}
      </div>
      <p className="mt-3 text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
        {t('alarm.start.hint')}
      </p>
    </Block>
  );
}

export function AlarmPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const alarm = useActiveAlarm(ctx.schoolId);
  const session = alarm.data ?? undefined;
  const board = useAlarmBoard(ctx.schoolId, session?.id, true);
  const actions = useAlarmActions();
  const today = todayIso();
  const myLessons = useLessons(
    ctx.schoolId,
    ctx.kind === 'teacher' ? { kind: 'teacher', memberId: ctx.memberId } : { kind: 'school' },
    today,
    today,
  );
  const now = useNow();
  const staff = ctx.kind === 'teacher' || ctx.kind === 'admin';

  // the class the teacher has right now (own or substituted lesson), else their homeroom class
  const current =
    ctx.kind === 'teacher'
      ? (myLessons.data ?? []).find(
          (l) => Date.parse(l.startsAt) <= now && now <= Date.parse(l.endsAt),
        )
      : undefined;
  const defaultClassId =
    current?.classId ?? (ctx.kind === 'teacher' ? ctx.homeroomClass?.id : undefined);
  const calmHint =
    ctx.kind === 'student' ||
    ctx.kind === 'guardian' ||
    ctx.kind === 'teacher' ||
    ctx.kind === 'admin'
      ? t(`alarm.calmHint.${ctx.kind}`)
      : undefined;

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('alarm.title')} />
      {alarm.isPending ? (
        <Spinner />
      ) : session ? (
        <>
          {/* the alarm itself, big: what, since when, what to do now */}
          <section
            role="alert"
            className="flex min-w-0 flex-col gap-5 rounded-[28px] p-5 md:p-7 lg:flex-row lg:items-center"
            style={{
              backgroundImage: alarmGround(session.kind === 'drill'),
              color: session.kind === 'drill' ? INK : '#FFFFFF',
            }}
          >
            <div className="flex min-w-0 flex-1 items-start gap-4 md:gap-5">
              <span className="md:hidden">
                <SirenMark drill={session.kind === 'drill'} />
              </span>
              <span className="hidden md:block">
                <SirenMark drill={session.kind === 'drill'} size={76} />
              </span>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-display text-[34px] leading-[40px] font-light tracking-[-0.04em] md:text-[46px] md:leading-[52px]">
                    {t(`alarm.kinds.${session.kind}`)}
                  </span>
                  {session.source === 'simulator' && (
                    <span
                      className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] font-medium"
                      style={{
                        backgroundColor:
                          session.kind === 'drill'
                            ? 'rgb(255 255 255 / 0.7)'
                            : 'rgb(255 255 255 / 0.22)',
                      }}
                    >
                      {t('alarm.simulated')}
                    </span>
                  )}
                </p>
                <p className="mt-1 text-[15px] leading-[22px] opacity-90">
                  {timeOf(session.started_at)} ·{' '}
                  {t('alarm.duration', { minutes: minutesSince(session.started_at, now) })}
                </p>
                <p className="mt-3 max-w-[640px] text-[17px] leading-[25px] font-medium">
                  {ctx.kind === 'guardian'
                    ? t('alarm.banner.noPickup')
                    : staff
                      ? t('alarm.go.staff')
                      : t('studentHome.shapes.alarmGo')}
                </p>
                {(ctx.kind === 'guardian' || ctx.kind === 'student') && (
                  <p className="mt-1 text-[15px] leading-[22px]">
                    <ChildLine ctx={ctx} sessionId={session.id} />
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 empty:hidden">
              {ctx.kind === 'student' && (
                <button
                  type="button"
                  className={pillClass('light')}
                  onClick={() =>
                    actions.mark.mutate({
                      schoolId: ctx.schoolId,
                      sessionId: session.id,
                      marks: [{ studentId: ctx.child.memberId, status: 'safe_elsewhere' }],
                    })
                  }
                >
                  {t('alarm.imSafe')}
                  <PillIcon icon={CheckIcon} />
                </button>
              )}
              {ctx.kind === 'admin' &&
                (session.source === 'official' || session.source === 'backup' ? (
                  <span
                    className="inline-flex min-h-9 items-center rounded-[10px] px-3 py-1.5 text-[13.5px] font-medium"
                    style={{ backgroundColor: 'rgb(255 255 255 / 0.22)' }}
                  >
                    {t('alarm.endOfficial')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={pillClass('light')}
                    onClick={() => actions.end.mutate(session.id)}
                  >
                    {t('alarm.end')}
                    <PillIcon icon={CheckIcon} />
                  </button>
                ))}
            </div>
          </section>

          {staff && board.data && (
            <>
              {ctx.kind === 'admin' && (
                <Board
                  session={session}
                  classes={board.data.classes}
                  shelters={board.data.shelters}
                />
              )}
              <RollCall
                key={defaultClassId ?? 'none'}
                session={session}
                schoolId={ctx.schoolId}
                classes={board.data.classes}
                shelters={board.data.shelters}
                defaultClassId={defaultClassId}
              />
            </>
          )}
          {ctx.kind === 'admin' && <ControlHour schoolId={ctx.schoolId} />}
          {ctx.kind === 'student' && (
            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <Block>
                <Head title={t('care.calm.breathing')} />
                <p className="-mt-2 text-[14px] leading-5" style={{ color: INK_SOFT }}>
                  {t('care.calm.shelter')}
                </p>
                <Breathing />
              </Block>
              <Block>
                <Head title={t('shelter.title')} />
                <ShelterTasks grade={ctx.child.class?.grade} />
              </Block>
            </div>
          )}
        </>
      ) : (
        <>
          <section
            className="relative isolate flex min-h-[168px] min-w-0 flex-col justify-center overflow-hidden rounded-[28px] p-5 md:p-7"
            style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
          >
            <TaskArt
              kind="done"
              tone="mint"
              size={170}
              ghost
              className="pointer-events-none absolute -top-4 right-2 -z-10 md:right-10"
            />
            <p
              className="font-display text-[32px] leading-[38px] font-light tracking-[-0.035em] md:text-[40px] md:leading-[46px]"
              style={{ color: INK }}
            >
              {t('alarm.noActive')}
            </p>
            {calmHint && (
              <p className="mt-2 max-w-[560px] text-[15px] leading-[22px]">{calmHint}</p>
            )}
          </section>
          {ctx.kind === 'admin' && <StartPanel schoolId={ctx.schoolId} />}
          {ctx.kind === 'admin' && <ControlHour schoolId={ctx.schoolId} />}
          {staff && <History schoolId={ctx.schoolId} />}
        </>
      )}
    </div>
  );
}
